import { FC, ReactNode, useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Lightbulb, Megaphone, Handshake, X } from 'lucide-react';
import { useUserStore } from '@/stores/userStore';

export interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  requiredPlans?: string[];
}


interface SidebarProps {
  brandName?: string;
  navItems?: NavItem[];
  isOpen?: boolean;
  onClose?: () => void;
}

const defaultNavItems: NavItem[] = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboard className="w-5 h-5" />,
  },
  {
    to: '/topics',
    label: 'Market Insight',
    icon: <Lightbulb className="w-5 h-5" />,
    requiredPlans: ['SXM', 'MXM+SXM'], // example
  },
  {
    to: '/marketing',
    label: 'Marketing Automation',
    icon: <Megaphone className="w-5 h-5" />,
    requiredPlans: ['SXM', 'MXM+SXM'], // example
  },
  {
    to: '/sales',
    label: 'Sales Automation',
    icon: <Handshake className="w-5 h-5" />,
    requiredPlans: ['SXM', 'MXM+SXM'], // example
  },
];


/**
 * Professional Sidebar Navigation Component
 * Clean, enterprise-grade navigation with responsive behavior
 */
export const Sidebar: FC<SidebarProps> = ({
  navItems = defaultNavItems,
  isOpen = false,
  onClose,
}) => {
  const { user, loading, fetchUser } = useUserStore();

  // fetch user on first mount (only if not already loaded)
  useEffect(() => {
    if (!user) fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const planName = (user?.planName ?? '').toLowerCase();

  const filteredNavItems = useMemo(() => {
    // If still loading user, you can show nothing or show skeleton
    if (loading) return navItems;

    return navItems.filter((item) => {
      // ✅ Demo users and Internal staff get full access to all features
      const isInternal = user?.role === 'internal' || user?.role === 'admin';
      const isDemo = user?.status === 'demo' || user?.status === 'DEMO';

      if (isInternal || isDemo) return true;

      // if no restriction => show to everyone (or you can restrict to logged-in only)
      if (!item.requiredPlans?.length) return true;

      // user must have a planName to match
      if (!planName) return false;

      return item.requiredPlans.map(p => p.toLowerCase()).includes(planName);
    });
  }, [navItems, loading, planName, user?.status]);

  const navItemClass = ({ isActive }: { isActive: boolean }): string =>
    `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors rounded-md
     ${isActive ? 'bg-slate-700 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`;

  const handleNavClick = (): void => {
    onClose?.();
  };

  return (
    <aside
      className={`
        w-64 bg-slate-900 shadow-sm flex-shrink-0
        transform transition-transform duration-200 ease-in-out
        lg:translate-x-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full absolute lg:relative'}
      `}
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-6 h-16 border-b border-slate-700">
          <img src="/white-carion-group-logo.png" alt="Carion Logo" className="w-[150px]" />
          <button
            onClick={onClose}
            className="lg:hidden p-1.5 text-slate-400 hover:text-white transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-6 overflow-y-auto">
          <ul className="space-y-1">
            {filteredNavItems.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} className={navItemClass} onClick={handleNavClick}>
                  {item.icon}
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}

            {/* optional: show a message if user has no access */}
            {!loading && filteredNavItems.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-400">
                No features available for your plan.
              </li>
            )}
          </ul>
        </nav>

        <div className="px-6 py-4 border-t border-slate-700">
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} Carion Portal</p>
        </div>
      </div>
    </aside>
  );
};

